import CarrotClient from "lib/carrot-api"
import React, { useEffect, useState, useRef } from "react"
import { useQuery } from "react-query"
import Settings from "utils/CarrotConfig"

const RoutingInfoContext = React.createContext(null)
const client = new CarrotClient(Settings.CARROT_BACKEND_ADMIN_URL)
export const useRoutingInfo = () => React.useContext(RoutingInfoContext)

export function RoutingInfoContextProvider({ children }): JSX.Element {
  const dedupeRef = useRef(false)
  const { isLoading, error, data, refetch } = useQuery(
    "carrotPlansRoutingInfo",
    () => client.getCarrotPlansRoutingInfo(),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false
    }
  )

  const [initialLoading, setInitialLoading] = useState(isLoading)

  useEffect(() => {
    if (initialLoading) {
      setInitialLoading(isLoading)
    }
  }, [isLoading, initialLoading])

  async function startRoutingInfo(): Promise<void> {
    if (!dedupeRef.current) {
      dedupeRef.current = true
      await client.startRoutingInfoFlow().catch(() => (dedupeRef.current = false))
      refetch({ throwOnError: true })
      dedupeRef.current = false
    }
  }

  async function updateRoutingInfo(payload, retry = true): Promise<void> {
    await client.updateRoutingInfo(payload).catch(async (error) => {
      if (retry) {
        await startRoutingInfo()
        const catchUp = data || {}
        await updateRoutingInfo({ ...catchUp, ...payload }, false)
      } else {
        throw error
      }
    })
    await refetch({ throwOnError: true })
    // We could probably do something clever here to avoid a full refresh by just pushing to state what we just sent up to save
  }

  async function completeRoutingInfo(): Promise<void> {
    if (!data?.completedUtc && !dedupeRef.current) {
      dedupeRef.current = true
      await client.completeRoutingInfoFlow().catch(() => (dedupeRef.current = false))
      refetch({ throwOnError: true })
      dedupeRef.current = false
    }
  }

  async function setMemberScheduledInitialCarrotPlanExpertChat(): Promise<void> {
    await client.setMemberScheduledInitialCarrotPlanExpertChat()
    await refetch({ throwOnError: true })
  }

  function hasQuestionBeenSkipped(questionKey: string): boolean {
    return data.skipFields.includes(questionKey)
  }

  return (
    <RoutingInfoContext.Provider
      value={{
        error,
        isLoading,
        initialLoading,
        routingData: data,
        updateRoutingInfo,
        startRoutingInfo,
        completeRoutingInfo,
        setMemberScheduledInitialCarrotPlanExpertChat,
        hasQuestionBeenSkipped
      }}
    >
      {children}
    </RoutingInfoContext.Provider>
  )
}
